import React, { useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import { Trash2, Edit, Copy } from "lucide-react";

const brl = (n) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    n || 0
  );

const emptyForm = {
  nome: "",
  valor_inscricao: "",
  valor_dobra: "",
  inicio: "",
  fim: "",
  ordem: 0,
};

export default function Lotes() {
  const { currentId } = useCompetition();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const load = async () => {
    if (!currentId) return;
    const { data } = await api.get(`/competitions/${currentId}/lotes`);
    setItems(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [currentId]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        nome: form.nome,
        valor_inscricao: parseFloat(form.valor_inscricao || 0),
        valor_dobra: parseFloat(form.valor_dobra || 0),
        inicio: form.inicio,
        fim: form.fim,
        ordem: Number(form.ordem || items.length + 1),
      };
      if (editingId) await api.put(`/lotes/${editingId}`, payload);
      else await api.post(`/competitions/${currentId}/lotes`, payload);
      setForm(emptyForm);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este lote?")) return;
    await api.delete(`/lotes/${id}`);
    await load();
  };

  const seedPreset = async () => {
    if (!window.confirm("Cadastrar os 4 lotes padrão do calendário?")) return;
    const y = new Date().getFullYear();
    const presets = [
      { nome: "1º LOTE", valor_inscricao: 290, valor_dobra: 190, inicio: `${y}-06-15`, fim: `${y}-07-05`, ordem: 1 },
      { nome: "2º LOTE", valor_inscricao: 310, valor_dobra: 210, inicio: `${y}-07-06`, fim: `${y}-08-31`, ordem: 2 },
      { nome: "3º LOTE", valor_inscricao: 330, valor_dobra: 230, inicio: `${y}-09-01`, fim: `${y}-10-31`, ordem: 3 },
      { nome: "4º LOTE FINAL", valor_inscricao: 350, valor_dobra: 250, inicio: `${y}-11-01`, fim: `${y}-11-25`, ordem: 4 },
    ];
    for (const p of presets) {
      await api.post(`/competitions/${currentId}/lotes`, p);
    }
    await load();
  };

  const publicUrl = `${window.location.origin}/inscricao/${currentId || ""}`;
  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Preços & Prazos</div>
          <h1 className="font-display text-5xl font-black tracking-tight">
            LOTES
          </h1>
        </div>
        <CompetitionSelector />
      </div>

      <div className="card p-4 mb-6 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="overline mb-1">Link público de inscrição</div>
          <div
            data-testid="lote-public-link"
            className="text-sm break-all text-[var(--text-secondary)]"
          >
            {publicUrl}
          </div>
        </div>
        <button
          data-testid="lote-copy-link"
          className="btn-ghost flex items-center gap-2"
          onClick={copyLink}
          disabled={!currentId}
        >
          <Copy size={14} /> {copied ? "Copiado!" : "Copiar link"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="card p-5">
            <div className="overline mb-3">
              {editingId ? "Editar Lote" : "Novo Lote"}
            </div>
            <form onSubmit={submit} className="space-y-3">
              <input
                data-testid="lote-nome"
                required
                className="input-field"
                placeholder="Ex: 1º LOTE"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="overline block mb-1">Início</label>
                  <input
                    data-testid="lote-inicio"
                    type="date"
                    required
                    className="input-field"
                    value={form.inicio}
                    onChange={(e) => setForm({ ...form, inicio: e.target.value })}
                  />
                </div>
                <div>
                  <label className="overline block mb-1">Fim</label>
                  <input
                    data-testid="lote-fim"
                    type="date"
                    required
                    className="input-field"
                    value={form.fim}
                    onChange={(e) => setForm({ ...form, fim: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="overline block mb-1">Valor Inscrição (R$)</label>
                <input
                  data-testid="lote-valor-inscricao"
                  type="number"
                  step="0.01"
                  min={0}
                  required
                  className="input-field"
                  value={form.valor_inscricao}
                  onChange={(e) =>
                    setForm({ ...form, valor_inscricao: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="overline block mb-1">
                  Valor Dobra / Categoria Adicional (R$)
                </label>
                <input
                  data-testid="lote-valor-dobra"
                  type="number"
                  step="0.01"
                  min={0}
                  required
                  className="input-field"
                  value={form.valor_dobra}
                  onChange={(e) =>
                    setForm({ ...form, valor_dobra: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="overline block mb-1">Ordem</label>
                <input
                  data-testid="lote-ordem"
                  type="number"
                  min={0}
                  className="input-field"
                  value={form.ordem}
                  onChange={(e) => setForm({ ...form, ordem: e.target.value })}
                />
              </div>
              {error && (
                <div className="text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2">
                  {error}
                </div>
              )}
              <div className="flex gap-2">
                <button data-testid="lote-submit" className="btn-primary flex-1">
                  {editingId ? "Atualizar" : "Adicionar"}
                </button>
                {editingId && (
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => {
                      setEditingId(null);
                      setForm(emptyForm);
                    }}
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </form>

            {items.length === 0 && currentId && (
              <button
                data-testid="lote-seed-preset"
                type="button"
                className="btn-ghost w-full mt-3"
                onClick={seedPreset}
              >
                Aplicar 4 lotes padrão (calendário)
              </button>
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="card overflow-x-auto">
            <table className="table-dense">
              <thead>
                <tr>
                  <th className="w-14">#</th>
                  <th>Nome</th>
                  <th>Vigência</th>
                  <th>Inscrição</th>
                  <th>Dobra</th>
                  <th className="text-right pr-4">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id} data-testid={`lote-row-${l.id}`}>
                    <td className="tabular-nums font-display font-bold text-lg">
                      {l.ordem}
                    </td>
                    <td className="font-semibold">{l.nome}</td>
                    <td className="text-xs tabular-nums">
                      {l.inicio} → {l.fim}
                    </td>
                    <td className="tabular-nums">{brl(l.valor_inscricao)}</td>
                    <td className="tabular-nums">{brl(l.valor_dobra)}</td>
                    <td className="text-right pr-4">
                      <button
                        className="btn-ghost mr-1"
                        onClick={() => {
                          setEditingId(l.id);
                          setForm({
                            nome: l.nome,
                            valor_inscricao: l.valor_inscricao,
                            valor_dobra: l.valor_dobra,
                            inicio: l.inicio,
                            fim: l.fim,
                            ordem: l.ordem,
                          });
                        }}
                      >
                        <Edit size={14} />
                      </button>
                      <button className="btn-ghost" onClick={() => del(l.id)}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center py-8 text-[var(--text-secondary)]"
                    >
                      Nenhum lote cadastrado. Use o botão &quot;4 lotes padrão&quot; ou
                      cadastre manualmente.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
